import { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../../../../contexts/AuthContext.jsx';
import {
  CheckIcon,
  ClockIcon,
  ExclamationCircleIcon,
  EllipsisHorizontalIcon,
  ChevronRightIcon,
} from '@heroicons/react/20/solid';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { TOTP } from 'totp-generator';
import {
  primaryButtonClasses,
  secondaryButtonClasses,
} from '../../../../libs/sharedClassNames.js';
//For ones like walmart, just have a step we do to start that shows the email
// uiSchema override to show the step or not
// Show errors as extraErrors

//TODO add function to reset the step

const getPasswordFieldKeys = (schema) => {
  const properties = schema?.properties ?? {};
  return Object.keys(properties).filter(
    (key) => properties[key]?.format === 'password' || key === 'password'
  );
};

const statusButtonMap = {
  pending: 'Submit',
  submitted: 'Cancel',
  running: 'Cancel',
  finished: 'Reset',
  failed: 'Re-submit',
}

const statusTextMap = {
  pending: 'Awaiting Submit',
  submitted: 'Awaiting Processing...',
  running: 'Processing...',
  finished: 'Success!',
  failed: 'Failed!',
}

const getIcon = (status, isCurrentStep, isSubmitting = false) => {
  // Show loading spinner when submitting, awaiting processing, or processing
  if (isSubmitting || status === 'submitted' || status === 'running') {
    return (
      <div className='loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-6 w-6'></div>
    );
  }

  switch (status) {
    case 'pending':
      return (
        <ClockIcon
          className={`h-6 w-6 ${
            isCurrentStep ? 'text-yellow-500' : 'text-gray-400'
          }`}
        />
      );
    case 'finished':
      return <CheckIcon className={`h-6 w-6 text-green-500`} />;
    case 'failed':
      return (
        <ExclamationCircleIcon
          className={`h-6 w-6 ${
            isCurrentStep ? 'text-red-500' : 'text-gray-400'
          }`}
        />
      );
    default:
      return (
        <ClockIcon
          className={`h-6 w-6 ${
            isCurrentStep ? 'text-yellow-500' : 'text-gray-400'
          }`}
        />
      );
  }
};

const generateUISchema = (step, isSubmitting = false, totpMessage = null, hasEditPermission = true) => {
  const { isMuffinAdmin, organization } = useAuthContext();
  const hasGlobalEditPermission = organization?.permissions?.integrations == 'edit';
  const { status, form_values, step_template, prompt_values } = step;

  const { schema, owner } = step_template;

  let isDisabled = isSubmitting;  
  let noRenderSubmit = false;

  const statusText = '**Status**: ' + (isSubmitting ? 'Submitting...' : statusTextMap[status]);

  const errorMessage = status === 'failed' && prompt_values?.message ? `**Error:** ${prompt_values?.message}` : '';

  // const messageText = prompt_values?.message;

  const fields = Object.keys(schema.properties ?? {});
  const nonReadOnlyFields = fields.filter(field => !schema?.properties?.[field]?.['ui:options']?.readonly);

  //Check if any keys
  if (owner !== 'user' && !isMuffinAdmin) {
    noRenderSubmit = true;
  } else if ((nonReadOnlyFields.length === 0) && status !== 'pending') {
    noRenderSubmit = true;
  }

  // Disable submit if user doesn't have edit permission on the integration
  if (isMuffinAdmin) {
    isDisabled = false;
  } else if (owner === 'user' && (!hasGlobalEditPermission || !hasEditPermission)) {
    isDisabled = true;
  }

  const uiSchema = {
    'ui:globalOptions': {
      enableMarkdownInDescription: true,
    },
  };

  const topLevelSchema = { ...(schema['ui:options'] || {}) };

  // Hide the form's built-in title since we render it separately with the badge
  topLevelSchema['title'] = '';

  if (topLevelSchema['description']) {
    // Remove any existing status text and add the current one
    const cleanDescription = topLevelSchema['description'].replace(/\*\*Status\*\*:.*?\n\n/, '');
    topLevelSchema['description'] = `${statusText} ${errorMessage}\n\n${cleanDescription}`;
  }

  topLevelSchema['submitButtonOptions'] = {
    norender: noRenderSubmit,
    ...(topLevelSchema['submitButtonOptions'] || {}),
    props: {
      className: primaryButtonClasses + ' box-shadow disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed',
      disabled: isSubmitting || isDisabled,
    },
    submitText: isSubmitting ? 'Submitting...' : statusButtonMap[status],
  };

  uiSchema['ui:options'] = topLevelSchema;

  const passwordFieldKeys = getPasswordFieldKeys(schema);

  // Process properties
  if (schema.properties) {
    Object.entries(schema.properties).forEach(([key, prop]) => {
      const uiOptions = prop['ui:options'] || {};
      uiSchema[key] = {'ui:options': uiOptions};
      const isPasswordField = passwordFieldKeys.includes(key);
      if (!isPasswordField && ['pending', 'failed'].includes(status) && form_values && form_values[key] && !uiOptions?.readonly) {
        uiSchema[key]['ui:options'].help = `Previous value: ${form_values[key]}`;
      }
      // Update totpCode help text with countdown
      if (key === 'totpCode' && totpMessage !== null) {
        uiSchema[key]['ui:options'].help = totpMessage;
      }
    });
  }
  return uiSchema;
};




const OwnerBadge = ({ owner }) => {
  const isUserStep = owner === 'user';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isUserStep
          ? 'bg-blue-100 text-blue-800'
          : 'bg-green-50 text-green-700'
      }`}
    >
      {isUserStep ? 'You' : 'Muffin'}
    </span>
  );
};

const Step = ({ step, isCurrentStep, isLastStep, handleSubmit, isSubmitting, permissions = [] }) => {
  const { organization } = useAuthContext();
  const hasEditPermission = permissions.includes('edit');
  const {
    step_template,
    status,
    prompt_values,
    form_values,
  } = step;
  const { schema } = step_template;
  const passwordFieldKeys = getPasswordFieldKeys(schema);
  let newFormValues = form_values;
  const hasPasswordValue =
    newFormValues &&
    passwordFieldKeys.some((key) => newFormValues[key]);
  if (hasPasswordValue) {
    if (status === 'failed') {
      newFormValues = { ...newFormValues };
      passwordFieldKeys.forEach((key) => {
        newFormValues[key] = null;
      });
    } else if (status === 'pending') {
      newFormValues = null;
    } else {
      newFormValues = { ...newFormValues };
      passwordFieldKeys.forEach((key) => {
        if (newFormValues[key]) {
          newFormValues[key] = '************';
        }
      });
    }
  }
  const [formData, setFormData] = useState(newFormValues);
  const [totpMessage, setTotpMessage] = useState(null);
  const totpSecretRef = useRef(null);
  const hasTotp = schema.properties && schema.properties.totp;

  if (schema?.properties?.muffinEmail) {
    schema.properties.muffinEmail.default = organization?.primary_ingestion_address;
  }
  if (schema?.properties?.mfaPhone) {
    schema.properties.mfaPhone.default = organization?.ingestion_phone_number;
  }

  if (hasTotp) {
    schema.properties.totpCode = {
      type: 'string',
      minLength: 6,
      maxLength: 6,
      readOnly: true,
      'ui:options': {
        label: false,
        placeholder: '000000',
        help: 'generated code',
      },
    };
    if (!schema['ui:options'].order.includes('totpCode')) {
      schema['ui:options'].order.push('totpCode');
    }
  }
  const shouldShowSchema = (isCurrentStep || status === 'finished') ?? false;
  const [showSchema, setShowSchema] = useState(shouldShowSchema);
  const readOnly = ['running', 'submitted', 'finished'].includes(status) || isSubmitting;

  useEffect(() => {
    const handleActionLinks = (e) => {
      if (e.target.tagName === 'A' && e.target.href.includes('docs:')) {
        e.preventDefault();
        const articleId = e.target.href.split('docs:')[1];
        if (window.Intercom && articleId) {
          window.Intercom('showArticle', articleId);
        } else if (window.Intercom) {
          // Fallback to showing general help if no article ID
          window.Intercom('show');
        }
      } else if (e.target.tagName === 'A' && e.target.href.includes('open:')) {
        e.preventDefault();
        const toOpen = e.target.href.split('open:')[1];
        if (toOpen === 'support') {
          if (window.Intercom) {
            window.Intercom('showNewMessage');
          } else {
            console.warn('Intercom is not initialized');
          }
        }
      }
    };

    document.addEventListener('click', handleActionLinks);
    return () => document.removeEventListener('click', handleActionLinks);
  }, []);

  // Update showSchema when isCurrentStep or status changes
  useEffect(() => {
    setShowSchema(shouldShowSchema);
  }, [shouldShowSchema, status]);

  // Generate TOTP code and calculate expiration time
  const generateTotpCode = async (totpSecret) => {
    if (!totpSecret || totpSecret === '' || totpSecret === null || totpSecret === undefined) return null;
    // Strip non-alphanumeric characters (keep letters and digits)
    const cleanedSecret = String(totpSecret).replace(/[^a-zA-Z0-9]/g, '');
    if (cleanedSecret.length < 10) return null;
    let totp = null;
    try {
      totp = await TOTP.generate(cleanedSecret);
    } catch (error) {
      console.error('Error generating TOTP code:', error);
      return { code: null, secondsRemaining: null, error: 'Invalid Key' };
    }
    // TOTP codes expire every 30 seconds (based on time windows)
    // Calculate time remaining until next expiration
    const now = Math.floor(Date.now() / 1000);
    const timeWindow = 30; // TOTP codes change every 30 seconds
    const currentWindow = Math.floor(now / timeWindow);
    const nextExpiration = (currentWindow + 1) * timeWindow;
    const secondsRemaining = nextExpiration - now;
    
    return { code: totp.otp, secondsRemaining, error: null };
  };

  // Effect to refresh TOTP code when totp exists
  useEffect(() => {
    if (!hasTotp || !formData?.totp || formData.totp === '' || formData.totp === null || formData.totp === undefined) {
      setTotpMessage(null);
      totpSecretRef.current = null;
      return;
    }

    // Strip non-alphanumeric characters and check length (consistent with handleChange)
    const cleanedTotp = String(formData.totp).replace(/[^a-zA-Z0-9]/g, '');
    if (cleanedTotp.length < 10) {
      setTotpMessage(null);
      totpSecretRef.current = null;
      return;
    }

    // Store cleaned totp secret in ref to avoid stale closures
    totpSecretRef.current = cleanedTotp;

    // Generate initial code
    const updateTotp = async () => {
      const secret = totpSecretRef.current;
      if (!secret) return;
      
      const { code : totpCode, secondsRemaining, error } = await generateTotpCode(secret);
      if (totpCode && !error) {
        setFormData(prev => ({ ...prev, totpCode }));
        setTotpMessage(`generated code expires in ${secondsRemaining}s`);
      } else {
        setFormData(prev => ({ ...prev, totpCode: null }));
        setTotpMessage(error);
      }
    };

    updateTotp();

    // Set up interval to refresh every second (for countdown) and regenerate when expired
    const interval = setInterval(async () => {
      const secret = totpSecretRef.current;
      if (!secret) return;
      
      const { code : totpCode, secondsRemaining, error } = await generateTotpCode(secret);
      if (totpCode && !error) {
        setFormData(prev => {
          // Only update if code changed (new time window)
          if (prev.totpCode !== totpCode) {
            return { ...prev, totpCode };
          }
          return prev;
        });
        setTotpMessage(`generated code expires in ${secondsRemaining}s`);
      } else {
        setFormData(prev => ({ ...prev, totpCode: null }));
        setTotpMessage(error);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [formData?.totp, hasTotp]);

  const handleChange = async(formData) => {
    if (formData && formData.totp !== undefined && formData.totp !== null) {
      // Strip non-alphanumeric characters from totp input (keep letters and digits)
      const originalTotp = formData.totp;
      const cleanedTotp = String(originalTotp).replace(/[^a-zA-Z0-9]/g,'');
      
      // Create a new object to ensure React detects the change
      const updatedFormData = { ...formData, totp: cleanedTotp };
      
      if (cleanedTotp.length >= 10) {
        const { code : totpCode, secondsRemaining, error } = await generateTotpCode(cleanedTotp);
        if (totpCode && !error) {
          updatedFormData.totpCode = totpCode;
          setTotpMessage(`generated code expires in ${secondsRemaining}s`);
        } else {
          // If generation fails, clear the code
          if (updatedFormData.totpCode) {
            delete updatedFormData.totpCode;
          }
          setTotpMessage(error);
        }
      } else {
        // Clear totpCode if length is less than 10
        if (updatedFormData.totpCode) {
          delete updatedFormData.totpCode;
        }
        setTotpMessage(null);
      }
      setFormData(updatedFormData);
    } else {
      setFormData(formData);
    }
  };

  return (
    <div className='relative flex mb-2 min-w-0 flex-1'>
      {/* Left column - Icon and line */}
      <div className='flex flex-col items-center mr-4'>
        <div className='flex-shrink-0'>{getIcon(status, isCurrentStep, isSubmitting)}</div>
        {!isLastStep ? (
          <span className='w-0.5 h-full bg-gray-200 mt-2' aria-hidden='true' />
        ) : null}
      </div>
      {/* Right column - Content */}
      <div className='flex-1 items-center'>
        <>
          {showSchema ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <h1 className='text-xl font-semibold text-gray-900'>
                  {schema['ui:options'].title}
                </h1>
                <OwnerBadge owner={step_template.owner} />
              </div>
              <div className={`prose ${isSubmitting ? 'pointer-events-none' : ''}`}>
                <Form
                  schema={schema}
                  validator={validator}
                  // templates={customTemplates}
                  onSubmit={(e) => handleSubmit(e, step)}
                  formData={formData}
                  onChange={(e) => handleChange(e.formData)}
                  uiSchema={generateUISchema(step, isSubmitting, totpMessage, hasEditPermission)}
                  readonly={readOnly}
                  // extraErrors={errorText ? {'__errors' : [errorText]} : []}
                  showErrorList={false}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <h1 className='text-xl font-semibold text-gray-400'>
                  {schema['ui:options'].title}
                </h1>
                <OwnerBadge owner={step_template.owner} />
              </div>
              {status === 'finished' && (
                <>
                  <button
                    className='text-blue-600 hover:underline focus:outline-none flex items-center gap-1'
                    onClick={() => setShowSchema(true)}
                  >
                    <ChevronRightIcon className='h-4 w-4' /> Show
                  </button>
                </>
              )}
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default Step;
